import IncredableContent from "../../components/incredable-content";
import { useContext, useState } from "react";
import {
  Button,
  ConfigProvider,
  Dropdown,
  Modal,
  Space,
  Typography,
  Form,
  Flex,
  Row,
  Col,
} from "antd";
import { Header, Table } from "@cloudscape-design/components";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { NavLink, useNavigate } from "react-router-dom";
import { SpinnerContext } from "../../context/spinner/spinner";
import { ToastContext } from "../../context/toast/incredable-toast";
import {
  useDeleteRequestTemplateMutation,
  useGetAllRequestTemplatesQuery,
  useSaveRequestTemplateMutation,
} from "../../redux/api/requesttemplate/requesttemplate";
import { FormProvider, useForm } from "react-hook-form";
import RHFTextField from "../../components/RHF/RHFTextField";
import DeleteAlertModal from "../../common/Modal/delete-alert-modal";
import RHFTextArea from "../../components/RHF/RHFTextArea";

type RequestTemplate = {
  id: string;
  name: string;
  description: string;
};

type FormInputType = {
  name: string;
  description: string;
};
export default function RequestTemplates() {
  const { data, refetch, isLoading } = useGetAllRequestTemplatesQuery();
  const [selectedRows, setSelectedRows] = useState<RequestTemplate[]>([]);
  const navigate = useNavigate();
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [openAddTemplateModal, setAddTemplateModal] = useState(false);
  const methods = useForm<FormInputType>();
  const [saveRequestTemplate] = useSaveRequestTemplateMutation();
  const [deleteRequestTemplate] = useDeleteRequestTemplateMutation();

  return (
    <IncredableContent>
      <Table
        loading={isLoading}
        onSelectionChange={({ detail }) => {
          setSelectedRows([...detail.selectedItems]);
        }}
        onRowClick={(onRowClickDetails) => {
          const selectedItem = onRowClickDetails?.detail?.item;
          if (selectedItem) {
            if (selectedRows?.at(0)?.id === selectedItem?.id) {
              setSelectedRows([]);
            } else {
              setSelectedRows([selectedItem]);
            }
          }
        }}
        selectedItems={selectedRows || []}
        columnDefinitions={[
          {
            id: "name",
            header: "Name",
            cell: (item: RequestTemplate) => (
              <NavLink to={`/requesttemplates/${item?.id}/edit`}>
                {item?.name}
              </NavLink>
            ),
            sortingField: "name",
            sortingComparator: (a, b) =>
              (a?.name ?? "").localeCompare(b?.name ?? ""),
          },
          {
            id: "description",
            header: "Description",
            cell: (item: RequestTemplate) => <span>{item?.description}</span>,
          },
        ]}
        items={data ?? []}
        loadingText="Loading resources"
        selectionType="single"
        trackBy="id"
        header={
          <div style={{ marginTop: "8px", marginBottom: "12px" }}>
            <Header
              actions={
                <Space size="middle">
                  <Button
                    onClick={() => {
                      !!refetch && refetch();
                    }}
                  >
                    <ReloadOutlined />
                  </Button>
                  <Dropdown
                    menu={{
                      disabled: selectedRows?.length !== 1,
                      items: [
                        {
                          label: "Edit",
                          key: "edit",
                          icon: <EditOutlined />,
                          onClick: () => {
                            navigate(`${selectedRows?.at(0)?.id}/edit`);
                          },
                        },
                        {
                          label: "Delete",
                          key: "delete",
                          onClick: () => setIsDeleteModalOpen(true),
                          icon: <DeleteOutlined />,
                          danger: true,
                        },
                      ],
                    }}
                  >
                    <Button>
                      Actions
                      <DownOutlined />
                    </Button>
                  </Dropdown>
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: "#2B6BE6",
                      },
                    }}
                  >
                    <Button
                      onClick={() => {
                        setAddTemplateModal(true);
                      }}
                      type="primary"
                      icon={<PlusOutlined />}
                    >
                      Add Request template
                    </Button>
                  </ConfigProvider>
                </Space>
              }
            >
              <Typography.Title level={3} style={{ marginTop: "0" }}>
                Request Templates{" "}
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: "18px" }}
                >{`(${data?.length || 0})`}</Typography.Text>
              </Typography.Title>
            </Header>
          </div>
        }
      />
      <Modal
        title={<Header>Add New Template</Header>}
        open={openAddTemplateModal}
        onOk={methods.handleSubmit(async (data: FormInputType) => {
          setAddTemplateModal(false);
          spinnerContext?.showSpinner();
          await saveRequestTemplate({
            name: data?.name,
            description: data?.description,
          });
          toastContext?.openInfoNotification({
            message: `Request template saved successfully`,
            placement: "topRight",
          });
          spinnerContext?.hidespinner();
          methods.reset();
        })}
        onCancel={() => {
          setAddTemplateModal(false);
          methods.reset();
        }}
        okText="Save"
        zIndex={1000}
      >
        <FormProvider {...methods}>
          <Form name="addrequesttemplate" layout="vertical" autoComplete="off">
            <Col span={24}>
              <RHFTextField
                formItemProps={{
                  label: "Name",
                }}
                inputProps={{}}
                controllerProps={{
                  name: `name`,
                }}
                rules={{ required: "This field is required" }}
              />
            </Col>
            <Col span={24}>
              <RHFTextArea
                formItemProps={{
                  label: "Description",
                }}
                textAreaProps={{}}
                controllerProps={{
                  name: `description`,
                }}
              />
            </Col>
          </Form>
        </FormProvider>
      </Modal>
      <DeleteAlertModal
        visible={isDeleteModalOpen}
        action={async () => {
          if (selectedRows?.at(0)?.id) {
            setIsDeleteModalOpen(false);
            spinnerContext?.showSpinner();
            try {
              await deleteRequestTemplate({
                id: String(selectedRows?.at(0)?.id),
              }).unwrap();
              toastContext?.openSuccessNotification({
                message: `Request template deleted successfully`,
                placement: "topRight",
              });
            } catch (error: any) {
              toastContext?.openErrorNotification({
                message:
                  error?.data?.errorMessage ||
                  `Failed to delete request template`,
                placement: "topRight",
              });
            }
            spinnerContext?.hidespinner();
            setSelectedRows([]);
          }
        }}
        closeModal={() => setIsDeleteModalOpen(false)}
        header="Delete Request Template"
        content={
          <Typography.Text>
            Name:{" "}
            <Typography.Text strong>
              {selectedRows?.at(0)?.name}
            </Typography.Text>
          </Typography.Text>
        }
        description="Are you sure you want to delete this request template?"
      />
    </IncredableContent>
  );
}
